import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  IconButton,
  Menu,
  MenuItem,
  TextField,
  InputAdornment,
  Grid,
  Alert,
  Snackbar,
  FormControl,
  InputLabel,
  Select,
  CircularProgress,
  Avatar
} from '@mui/material';
import {
  Add as AddIcon,
  Search as SearchIcon,
  MoreVert as MoreVertIcon,
  Visibility as ViewIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Receipt as ReceiptIcon,
  CheckCircle as ApproveIcon,
  Cancel as RejectIcon,
  Upload as UploadIcon,
  FilterList as FilterIcon,
  AttachMoney as MoneyIcon,
  CloudUpload as ImportIcon
} from '@mui/icons-material';

import CreateExpenseDialog from './CreateExpenseDialog';
import ViewExpenseDialog from './ViewExpenseDialog';
import { ImportWizard } from '../import';

// Types
interface Expense {
  id: number;
  expense_number: string;
  employee_id: number;
  employee_name: string;
  employee_email?: string;
  category_id: number;
  category_name: string;
  expense_date: string;
  description: string;
  net_amount: number;
  vat_rate: string;
  vat_amount: number;
  total_amount: number;
  status: string;
  merchant_name?: string;
  business_purpose?: string;
  notes?: string;
  receipt_url?: string;
  submitted_date?: string;
  approved_date?: string;
  approved_by?: string;
  net_amount_formatted: string;
  vat_amount_formatted: string;
  total_amount_formatted: string;
}

interface Employee {
  id: number;
  first_name: string;
  last_name: string;
  email: string;
  department: string;
}

interface ExpenseCategory {
  id: number;
  name: string;
  description?: string;
  is_active: boolean;
}

const ExpenseManagement: React.FC = () => {
  // State
  const [expenses, setExpenses] = useState<Expense[]>([]);
  const [employees, setEmployees] = useState<Employee[]>([]);
  const [categories, setCategories] = useState<ExpenseCategory[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [employeeFilter, setEmployeeFilter] = useState('');
  const [selectedExpense, setSelectedExpense] = useState<Expense | null>(null);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [showViewDialog, setShowViewDialog] = useState(false);
  const [showImportDialog, setShowImportDialog] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' as 'success' | 'error' });

  // Pagination and sorting
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [sortBy, setSortBy] = useState('expense_date');
  const [sortOrder, setSortOrder] = useState('desc');

  useEffect(() => {
    fetchExpenses();
    fetchEmployees();
    fetchCategories();
  }, [searchTerm, statusFilter, employeeFilter, sortBy, sortOrder, page, rowsPerPage]);

  const fetchExpenses = async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams({
        skip: (page * rowsPerPage).toString(),
        limit: rowsPerPage.toString(),
        sort_by: sortBy,
        sort_order: sortOrder,
      });

      if (searchTerm) params.append('search', searchTerm);
      if (statusFilter) params.append('status', statusFilter);
      if (employeeFilter) params.append('employee_id', employeeFilter);

      const response = await fetch(`/api/v1/expenses/?${params}`);
      if (response.ok) {
        const data = await response.json();
        setExpenses(data.expenses || []);
      } else {
        throw new Error('Failed to fetch expenses');
      }
    } catch (error) {
      console.error('Error fetching expenses:', error);
      setSnackbar({ open: true, message: 'Failed to load expenses', severity: 'error' });
    } finally {
      setLoading(false);
    }
  };

  const fetchEmployees = async () => {
    try {
      const response = await fetch('/api/v1/employees/');
      if (response.ok) {
        const data = await response.json();
        setEmployees(data.employees || []);
      }
    } catch (error) {
      console.error('Error fetching employees:', error);
    }
  };

  const fetchCategories = async () => {
    try {
      const response = await fetch('/api/v1/expenses/categories');
      if (response.ok) {
        const data = await response.json();
        setCategories(data || []);
      }
    } catch (error) {
      console.error('Error fetching categories:', error);
    }
  };

  const handleMenuClick = (event: React.MouseEvent<HTMLElement>, expense: Expense) => {
    setAnchorEl(event.currentTarget);
    setSelectedExpense(expense);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
    setSelectedExpense(null);
  };

  const handleViewExpense = () => {
    setShowViewDialog(true);
    handleMenuClose();
  };

  const handleApproveExpense = async () => {
    if (!selectedExpense) return;

    try {
      const response = await fetch(`/api/v1/expenses/${selectedExpense.id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ status: 'approved' })
      });

      if (response.ok) {
        setSnackbar({ open: true, message: 'Expense approved successfully!', severity: 'success' });
        fetchExpenses();
      } else {
        throw new Error('Failed to approve expense');
      }
    } catch (error) {
      setSnackbar({ open: true, message: 'Failed to approve expense', severity: 'error' });
    }
    handleMenuClose();
  };

  const getStatusColor = (status: string) => {
    switch (status.toLowerCase()) {
      case 'draft': return 'default';
      case 'submitted': return 'info';
      case 'approved': return 'success';
      case 'rejected': return 'error';
      case 'paid': return 'success';
      default: return 'default';
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status.toLowerCase()) {
      case 'draft': return '📝';
      case 'submitted': return '📤';
      case 'approved': return '✅';
      case 'rejected': return '❌';
      case 'paid': return '💰';
      default: return '📄';
    }
  };

  const formatVATRate = (vatRate: string) => {
    switch (vatRate) {
      case 'STANDARD': return '20%';
      case 'REDUCED': return '5%';
      case 'ZERO': return '0%';
      case 'EXEMPT': return 'Exempt';
      default: return vatRate;
    }
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      {/* Header */}
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h4" component="h1">
          Expense Management
        </Typography>
        <Box display="flex" gap={2}>
          <Button
            variant="outlined"
            startIcon={<ImportIcon />}
            onClick={() => setShowImportDialog(true)}
          >
            Import Expenses
          </Button>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => setShowCreateDialog(true)}
            sx={{ minWidth: 150 }}
          >
            Add Expense
          </Button>
        </Box>
      </Box>

      {/* Filters and Search */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} md={3}>
              <TextField
                fullWidth
                placeholder="Search expenses..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon />
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>
            <Grid item xs={12} md={2}>
              <FormControl fullWidth>
                <InputLabel>Status</InputLabel>
                <Select
                  value={statusFilter}
                  label="Status"
                  onChange={(e) => setStatusFilter(e.target.value)}
                >
                  <MenuItem value="">All Statuses</MenuItem>
                  <MenuItem value="draft">Draft</MenuItem>
                  <MenuItem value="submitted">Submitted</MenuItem>
                  <MenuItem value="approved">Approved</MenuItem>
                  <MenuItem value="rejected">Rejected</MenuItem>
                  <MenuItem value="paid">Paid</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={2}>
              <FormControl fullWidth>
                <InputLabel>Employee</InputLabel>
                <Select
                  value={employeeFilter}
                  label="Employee"
                  onChange={(e) => setEmployeeFilter(e.target.value)}
                >
                  <MenuItem value="">All Employees</MenuItem>
                  {employees.map((employee) => (
                    <MenuItem key={employee.id} value={employee.id.toString()}>
                      {employee.first_name} {employee.last_name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={2}>
              <Button
                variant="outlined"
                startIcon={<FilterIcon />}
                fullWidth
              >
                More Filters
              </Button>
            </Grid>
            <Grid item xs={12} md={3}>
              <Typography variant="body2" color="textSecondary">
                {expenses.length} expense{expenses.length !== 1 ? 's' : ''} found
              </Typography>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* Expenses Table */}
      <Card>
        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Expense #</TableCell>
                <TableCell>Employee</TableCell>
                <TableCell>Date</TableCell>
                <TableCell>Description</TableCell>
                <TableCell>Category</TableCell>
                <TableCell>Status</TableCell>
                <TableCell align="right">Amount</TableCell>
                <TableCell align="right">VAT</TableCell>
                <TableCell align="center">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {expenses.map((expense) => (
                <TableRow key={expense.id} hover>
                  <TableCell>
                    <Typography variant="body2" fontWeight="medium">
                      {expense.expense_number}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Box display="flex" alignItems="center" gap={1}>
                      <Avatar sx={{ width: 32, height: 32, fontSize: '0.875rem' }}>
                        {expense.employee_name.split(' ').map(n => n[0]).join('')}
                      </Avatar>
                      <Box>
                        <Typography variant="body2">{expense.employee_name}</Typography>
                        {expense.employee_email && (
                          <Typography variant="caption" color="textSecondary">
                            {expense.employee_email}
                          </Typography>
                        )}
                      </Box>
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2">
                      {new Date(expense.expense_date).toLocaleDateString('en-GB')}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2">{expense.description}</Typography>
                    {expense.merchant_name && (
                      <Typography variant="caption" color="textSecondary">
                        {expense.merchant_name}
                      </Typography>
                    )}
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2">{expense.category_name}</Typography>
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={`${getStatusIcon(expense.status)} ${expense.status.toUpperCase()}`}
                      color={getStatusColor(expense.status) as any}
                      size="small"
                    />
                  </TableCell>
                  <TableCell align="right">
                    <Typography variant="body2" fontWeight="medium">
                      {expense.total_amount_formatted}
                    </Typography>
                    <Typography variant="caption" color="textSecondary">
                      Net: {expense.net_amount_formatted}
                    </Typography>
                  </TableCell>
                  <TableCell align="right">
                    <Typography variant="body2">
                      {expense.vat_amount_formatted}
                    </Typography>
                    <Typography variant="caption" color="textSecondary">
                      {formatVATRate(expense.vat_rate)}
                    </Typography>
                  </TableCell>
                  <TableCell align="center">
                    <IconButton
                      size="small"
                      onClick={(e) => handleMenuClick(e, expense)}
                    >
                      <MoreVertIcon />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Card>

      {/* Action Menu */}
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
      >
        <MenuItem onClick={handleViewExpense}>
          <ViewIcon sx={{ mr: 1 }} /> View Details
        </MenuItem>
        <MenuItem onClick={handleMenuClose}>
          <EditIcon sx={{ mr: 1 }} /> Edit Expense
        </MenuItem>
        {selectedExpense?.status === 'submitted' && (
          <>
            <MenuItem onClick={handleApproveExpense}>
              <ApproveIcon sx={{ mr: 1 }} /> Approve
            </MenuItem>
            <MenuItem onClick={handleMenuClose}>
              <RejectIcon sx={{ mr: 1 }} /> Reject
            </MenuItem>
          </>
        )}
        <MenuItem onClick={handleMenuClose}>
          <ReceiptIcon sx={{ mr: 1 }} /> View Receipt
        </MenuItem>
        <MenuItem onClick={handleMenuClose}>
          <UploadIcon sx={{ mr: 1 }} /> Upload Receipt
        </MenuItem>
      </Menu>

      {/* Create Expense Dialog */}
      <CreateExpenseDialog
        open={showCreateDialog}
        onClose={() => setShowCreateDialog(false)}
        onSuccess={() => {
          setSnackbar({ open: true, message: 'Expense created successfully!', severity: 'success' });
          fetchExpenses();
        }}
        employees={employees}
        categories={categories}
      />

      {/* View Expense Dialog */}
      <ViewExpenseDialog
        open={showViewDialog}
        onClose={() => setShowViewDialog(false)}
        expense={selectedExpense}
        onApprove={handleApproveExpense}
      />

      {/* Import Wizard */}
      <ImportWizard
        open={showImportDialog}
        onClose={() => {
          setShowImportDialog(false);
          fetchExpenses(); // Refresh expense list after import
        }}
        entityType="expense"
        onImportComplete={(result) => {
          setSnackbar({
            open: true,
            message: `Successfully imported ${result.imported_count} expenses!`,
            severity: 'success'
          });
        }}
      />

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
      >
        <Alert severity={snackbar.severity} onClose={() => setSnackbar({ ...snackbar, open: false })}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default ExpenseManagement;
